import { IconSearch } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import z from "zod";
import {
	DataTableGeneric,
	type DataTableSearchParams,
	DataTableSearchSchema,
} from "@/components/ui/data-table-generic";
import { Input } from "@/components/ui/input";
import { orpc } from "@/orpc/client";
import { columns } from "@/tables/channel-episodes";

export const Route = createFileRoute("/_app/channels/$channelId/episodes")({
	component: Page,
	validateSearch: (search) => DataTableSearchSchema.parse(search),
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
		context.queryClient.prefetchQuery(
			orpc.channels.listEpisodes.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const { data, isFetching, refetch } = useSuspenseQuery(
		orpc.channels.listEpisodes.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const [globalFilter, setGlobalFilter] = useState(search.q ?? "");

	const handleNavigate = useCallback(
		(params: DataTableSearchParams) => {
			navigate({
				replace: true,
				search: (prev) => ({
					...prev,
					...params,
				}),
			});
		},
		[navigate],
	);

	const toolbar = (
		<>
			<div className="relative">
				<Input
					value={globalFilter}
					onChange={(e) => {
						setGlobalFilter(e.target.value);
					}}
					placeholder="Search Episodes..."
					className="pl-10"
				/>
				<div className="pointer-events-none absolute top-0 left-2 flex h-full items-center justify-center">
					<IconSearch className="h-4" />
				</div>
			</div>
		</>
	);

	return (
		<div className="flex flex-1 flex-col">
			<div className="@container/main flex flex-1 flex-col gap-2">
				<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
					<DataTableGeneric
						columns={columns}
						data={data ?? []}
						isFetching={isFetching}
						handleReload={() => refetch()}
						toolbar={toolbar}
						searchParams={{
							sort: "ID:desc",
							...search,
							q: globalFilter,
						}}
						onNavigate={handleNavigate}
						getRowId={(row) => row.ID.toString()}
						config={{
							defaultPageSize: 25,
							pageSizeOptions: [25, 50, 100, 500],
						}}
					/>
				</div>
			</div>
		</div>
	);
}
